import * as css from './styles'

interface Props {
  previewValue: string
  position: string
  delimiter: string
}

/**
 * This function searches a string (str) for the nth (n) occurence of a
 * given sub-string/pattern (pat).
 *
 * @param {string} str string to be searched
 * @param {string} pat string to find within 'str'
 * @param {number} n nth occurence
 * @returns
 */
export function nthIndex(str, pat, n) {
  const L = str.length
  let i = -1
  while (n-- && i++ < L) {
    i = str.indexOf(pat, i)
    if (i < 0) break
  }
  return i
}

/**
 * This function splits the preview value and generates three different strings, based
 * on the delimiter position. These strings are used to create a markup in which the 'middle' value
 * is highlighted.
 * @param previewValue string to be split
 * @param delimiterStartIndexInString
 * @param delimiterEndIndexInString
 * @param {string} delimiter
 * @returns {Array<string>}
 */
export const buildPreviewValueParts = (
  previewValue,
  delimiterStartIndexInString,
  delimiterEndIndexInString,
  delimiter,
): string[] => {
  let first
  let middle
  let rest
  if (delimiterStartIndexInString === -1) {
    first = ''
  } else {
    first = previewValue.substring(0, delimiterStartIndexInString + delimiter.length)
  }

  // This means that there is only substring of the delimiter within the value.
  if (delimiterEndIndexInString === -1) {
    // This means the 0 position does not exists
    if (delimiterStartIndexInString === -1 && previewValue.indexOf(delimiter) === 0) {
      middle = ''
      rest = previewValue
    } else {
      middle = previewValue.substring(delimiterStartIndexInString + delimiter.length)
      rest = ''
    }
  } else {
    // This means the 0 position does not exists
    if (delimiterStartIndexInString === -1 && previewValue.indexOf(delimiter) === 0) {
      middle = ''
      rest = previewValue
    } else {
      middle = previewValue.substring(
        delimiterStartIndexInString + delimiter.length,
        delimiterEndIndexInString,
      )
      rest = previewValue.substring(delimiterEndIndexInString)
    }
  }

  return [first, middle, rest]
}

/**
 * This component generates <span><mark><span> tags, in which the <mark> tag
 * depends on nthe position and delimiter parameters.
 */
export const InputHighlightedValue = ({ previewValue, delimiter, position }: Props) => {
  if (!previewValue) return null

  if (
    !delimiter ||
    !position ||
    !previewValue.includes(delimiter) ||
    !(previewValue.split(delimiter).length > Number(position))
  ) {
    return <>{previewValue}</>
  }

  const delimiterStartIndexInString = nthIndex(previewValue, delimiter, Number(position))
  const delimiterEndIndexInString = previewValue.indexOf(
    delimiter,
    delimiterStartIndexInString + delimiter.length,
  )

  const [first, middle, rest] = buildPreviewValueParts(
    previewValue,
    delimiterStartIndexInString,
    delimiterEndIndexInString,
    delimiter,
  )

  return (
    <>
      <span>{first}</span>
      <css.MatchHighlight>{middle}</css.MatchHighlight>
      <span>{rest}</span>
    </>
  )
}
